import { rethink } from './client';
import type { DocumentData, Protocol } from './protocol';
import type { QuerySnapshot, DocumentSnapshot, FirestoreDataConverter } from './references';
import { CollectionReference, DocumentReference, Query } from './references';
import type { QueryConstraint } from './constraints';
import {
  WhereConstraint,
  OrderByConstraint,
  LimitConstraint,
  SelectConstraint,
  CursorConstraint,
} from './constraints';

export type { DocumentData, QuerySnapshot, DocumentSnapshot };
export { CollectionReference, DocumentReference, Query, type QueryConstraint };

// Mock DB object to satisfy the API signature
export const db = {};

// ==========================================
// 1. ATOMIC OPERATIONS & FIELD VALUES
// ==========================================

/**
 * FieldValue for atomic operations
 */
export class FieldValue {
  static increment(n: number) {
    return { __type: 'increment', n };
  }

  static arrayUnion(...elements: unknown[]) {
    return { __type: 'arrayUnion', elements };
  }

  static arrayRemove(...elements: unknown[]) {
    return { __type: 'arrayRemove', elements };
  }

  static serverTimestamp() {
    return { __type: 'serverTimestamp' };
  }

  static delete() {
    return { __type: 'deleteField' };
  }
}

export const increment = FieldValue.increment;
export const arrayUnion = FieldValue.arrayUnion;
export const arrayRemove = FieldValue.arrayRemove;
export const serverTimestamp = FieldValue.serverTimestamp;

/**
 * Mimics Firestore's deleteField()
 */
export function deleteField() {
  return FieldValue.delete();
}

// ==========================================
// 2. TIMESTAMPS
// ==========================================

/**
 * Firestore Timestamp mock
 */
export class Timestamp {
  constructor(
    public seconds: number,
    public nanoseconds: number,
  ) {}

  static now(): Timestamp {
    const n = Date.now();
    return new Timestamp(Math.floor(n / 1000), (n % 1000) * 1e6);
  }

  static fromDate(date: Date): Timestamp {
    const n = date.getTime();
    return new Timestamp(Math.floor(n / 1000), (n % 1000) * 1e6);
  }

  toDate(): Date {
    return new Date(this.seconds * 1000 + this.nanoseconds / 1e6);
  }

  toMillis(): number {
    return this.seconds * 1000 + this.nanoseconds / 1e6;
  }

  // Formats serializations cleanly as an ISO string for exact database matches
  toJSON(): string {
    return this.toDate().toISOString();
  }
}

// ==========================================
// 3. REFERENCES & QUERY CONSTRAINTS
// ==========================================

/**
 * Mimics Firestore's collection()
 */
export function collection(_db: unknown, name: string): CollectionReference {
  return rethink.collection(name);
}

/**
 * Mimics Firestore's collectionGroup()
 */
export function collectionGroup(_db: unknown, name: string): Query {
  return rethink.collectionGroup(name);
}

/**
 * Mimics Firestore's doc()
 */
export function doc(_db: unknown, collectionName: string, id: string): DocumentReference {
  return rethink.doc(collectionName, id);
}

/**
 * Mimics Firestore's where()
 */
export function where(field: string, op: Protocol.Op, value: unknown): WhereConstraint {
  return new WhereConstraint(field, op, value);
}

/**
 * Mimics Firestore's orderBy()
 */
export function orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): OrderByConstraint {
  return new OrderByConstraint(field, direction);
}

/**
 * Mimics Firestore's limit()
 */
export function limit(n: number): LimitConstraint {
  return new LimitConstraint(n);
}

/**
 * Cursor constraints
 */
export function startAt(value: unknown): CursorConstraint {
  return new CursorConstraint('startAt', value);
}
export function startAfter(value: unknown): CursorConstraint {
  return new CursorConstraint('startAfter', value);
}
export function endAt(value: unknown): CursorConstraint {
  return new CursorConstraint('endAt', value);
}
export function endBefore(value: unknown): CursorConstraint {
  return new CursorConstraint('endBefore', value);
}

/**
 * Custom API for projection pushdown
 */
export function select(...fields: string[]): SelectConstraint {
  return new SelectConstraint(fields);
}

/**
 * Mimics Firestore's query()
 */
export function query(
  colRef: CollectionReference | Query,
  ...constraints: QueryConstraint[]
): Query {
  const base = colRef instanceof Query ? colRef : new Query(colRef);
  return new Query(base.colRef, [...base.constraints, ...constraints], base.isGroup);
}

// ==========================================
// 4. DATA MUTATIONS & FETCHING
// ==========================================

/**
 * Mimics Firestore's getDocs()
 */
export async function getDocs(q: CollectionReference | Query): Promise<QuerySnapshot> {
  return rethink.getDocs(q);
}
export const getDocsFromServer = getDocs;

/**
 * Mimics Firestore's getDoc()
 */
export async function getDoc(docRef: DocumentReference): Promise<DocumentSnapshot> {
  return rethink.getDoc(docRef);
}
export const getDocFromServer = getDoc;

/**
 * Mimics Firestore's setDoc()
 */
export async function setDoc(
  docRef: DocumentReference,
  data: DocumentData,
  options: { merge?: boolean } = {},
): Promise<unknown> {
  return rethink.setDoc(docRef, data, options);
}

/**
 * Mimics Firestore's addDoc()
 */
export async function addDoc(colRef: CollectionReference, data: DocumentData): Promise<unknown> {
  return rethink.addDoc(colRef, data);
}

/**
 * Mimics Firestore's updateDoc()
 */
export async function updateDoc(docRef: DocumentReference, data: DocumentData): Promise<unknown> {
  return rethink.updateDoc(docRef, data);
}

/**
 * Mimics Firestore's deleteDoc()
 */
export async function deleteDoc(docRef: DocumentReference): Promise<unknown> {
  return rethink.deleteDoc(docRef);
}

// ==========================================
// 5. TRANSACTIONS & BATCHES
// ==========================================

/**
 * Mimics Firestore's writeBatch()
 */
export function writeBatch(_db: unknown): unknown {
  return rethink.writeBatch();
}

/**
 * Mimics Firestore's runTransaction()
 */
export async function runTransaction(
  _db: unknown,
  updateFunction: (transaction: unknown) => Promise<unknown>,
): Promise<unknown> {
  return rethink.runTransaction(updateFunction);
}

// ==========================================
// 6. AGGREGATIONS & REAL-TIME
// ==========================================

/**
 * Aggregation functions
 */
export async function getCountFromServer(
  q: CollectionReference | Query,
): Promise<{ data: () => unknown; count: number | undefined }> {
  return rethink.getCountFromServer(q);
}

export async function getSumFromServer(
  q: CollectionReference | Query,
  field: string,
): Promise<{ data: () => unknown; count: number | undefined }> {
  return rethink.getSumFromServer(q, field);
}

export async function getAverageFromServer(
  q: CollectionReference | Query,
  field: string,
): Promise<{ data: () => unknown; count: number | undefined }> {
  return rethink.getAverageFromServer(q, field);
}

/**
 * Mimics Firestore's onSnapshot()
 */
export function onSnapshot(
  q: CollectionReference | Query,
  onNext: (snapshot: QuerySnapshot) => void,
  onError?: (error: Error) => void,
): () => void {
  return rethink.onSnapshot(q, onNext, onError);
}

// ==========================================
// 7. CUSTOM SYSTEM & UTILITY APIs
// ==========================================

export function withConverter<T>(
  ref: CollectionReference,
  converter: FirestoreDataConverter<T>,
): CollectionReference<T> {
  return ref.withConverter(converter);
}

export async function listCollections(): Promise<string[]> {
  return rethink.listCollections();
}

export async function createCollection(name: string): Promise<{ success: boolean }> {
  return rethink.createCollection(name);
}

export async function getSystemInfo(): Promise<{ status: string; type: string }> {
  return rethink.getSystemInfo();
}

/**
 * Custom API for index creation
 */
export async function indexCreate(
  collectionName: string,
  indexName: string,
  fields: string[],
): Promise<unknown> {
  return rethink.indexCreate(collectionName, indexName, fields);
}
